/**
 * <vibe-gallery> — lightbox gallery for a published vibe.
 * Mobile: fullscreen A2-style swipe (one shot per view, swipe-down to dismiss).
 * Desktop: a coverflow lightbox — current shot centred + contained, dimmed
 * prev/next peeking; navigate via ‹ › arrows, ← → keys, or clicking a neighbour.
 * Reads #VibeData, builds slides lazily on first open, opens at a shot index,
 * dismisses via close / Esc. ATC via the cart-resolve proxy → /cart/add.js.
 */
class VibeGallery extends HTMLElement {
  connectedCallback() {
    this.data = this._readData();
    this._built = false;
    this._lastFocus = null;
    this._onKey = (e) => {
      if (e.key === 'Escape') { this.close(); return; }
      if (e.key === 'ArrowLeft') { this._go(-1); return; }
      if (e.key === 'ArrowRight') { this._go(1); return; }
      if (e.key === 'Tab') this._trapFocus(e);
    };
    // i18n strings from data attributes (Liquid renders these from t: keys).
    this._t = {
      close: this.dataset.tClose || 'Close',
      add: this.dataset.tAdd || 'Add',
      addTheSet: this.dataset.tAddTheSet || 'Add the set',
      theSet: this.dataset.tTheSet || 'The set',
      added: this.dataset.tAdded || 'Added',
      setAdded: this.dataset.tSetAdded || 'Set added',
      addedToCart: this.dataset.tAddedToCart || 'Added to cart',
      addError: this.dataset.tAddError || 'Something went wrong. Please try again.',
    };
    // Delegate card taps from the page. Bound property so it can be removed on teardown.
    this._onDocClick = (e) => {
      const card = e.target.closest('.vibe__shot-card');
      if (!card) return;
      this._lastFocus = card;
      this.open(Number(card.dataset.shotIndex) || 0);
    };
    document.addEventListener('click', this._onDocClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._onDocClick);
    document.removeEventListener('keydown', this._onKey);
    if (this._track && this._onScroll) this._track.removeEventListener('scroll', this._onScroll);
  }

  _readData() {
    try { return JSON.parse(document.getElementById('VibeData').textContent); }
    catch { return { shots: [] }; }
  }

  _money(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency', currency: window.Shopify?.currency?.active || 'GBP'
    }).format((cents || 0) / 100);
  }

  _build() {
    const track = document.createElement('div');
    track.className = 'vibe-gallery__track';
    this.data.shots.forEach((shot, i) => track.appendChild(this._renderSlide(shot, i)));

    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'vibe-gallery__close';
    close.setAttribute('aria-label', this._t.close);
    close.textContent = '✕';
    close.addEventListener('click', () => this.close());

    const toast = document.createElement('div');
    toast.className = 'vibe-gallery__toast';

    // Progress dots: one per shot. Skip for 0/1 shots (a single dot is noise).
    let dots = null;
    const count = this.data.shots.length;
    if (count > 1) {
      dots = document.createElement('div');
      dots.className = 'vibe-gallery__dots';
      for (let i = 0; i < count; i++) {
        const dot = document.createElement('i');
        dot.className = 'vibe-gallery__dot';
        dots.appendChild(dot);
      }
    }
    this._dots = dots;

    // Desktop coverflow nav arrows (hidden on mobile via CSS).
    const nav = ['prev', 'next'].map((dir) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = `vibe-gallery__nav vibe-gallery__nav--${dir}`;
      b.setAttribute('aria-label', dir === 'prev' ? 'Previous' : 'Next');
      b.textContent = dir === 'prev' ? '‹' : '›';
      b.addEventListener('click', () => this._go(dir === 'prev' ? -1 : 1));
      return b;
    });

    // Wrap the chrome + track in a frame: fullscreen swipe on mobile, a
    // coverflow lightbox on desktop (CSS). Chrome anchors to the frame.
    const frame = document.createElement('div');
    frame.className = 'vibe-gallery__frame';
    const frameChildren = [close, ...nav, track];
    if (dots) frameChildren.splice(1, 0, dots);
    frame.append(...frameChildren);
    this.replaceChildren(frame, toast);
    this._track = track;
    this._toast = toast;
    this._close = close;
    this._built = true;

    // Modal a11y: announce the overlay as a dialog.
    this.setAttribute('role', 'dialog');
    this.setAttribute('aria-modal', 'true');
    this.setAttribute('aria-label', 'Vibe gallery');

    // Swipe-down to dismiss (mobile).
    let startY = null;
    track.addEventListener('touchstart', (e) => { startY = e.touches[0].clientY; }, { passive: true });
    track.addEventListener('touchmove', (e) => {
      if (startY !== null && e.touches[0].clientY - startY > 90) { this.close(); startY = null; }
    }, { passive: true });

    // Track the centered slide on scroll → update dots + active (dim) state.
    this._onScroll = () => {
      const i = this._currentIndex();
      this._updateDots(i);
      this._setActive(i);
    };
    track.addEventListener('scroll', this._onScroll, { passive: true });
  }

  _updateDots(index) {
    if (!this._dots) return;
    Array.from(this._dots.children).forEach((dot, i) => {
      dot.classList.toggle('is-on', i === index);
    });
  }

  // The slide whose centre is nearest the track's viewport centre.
  // Works for full-width (mobile) and peeking (desktop) layouts alike.
  _currentIndex() {
    if (!this._track) return 0;
    const center = this._track.scrollLeft + this._track.clientWidth / 2;
    let best = 0;
    let bestDist = Infinity;
    Array.from(this._track.children).forEach((sl, i) => {
      const c = sl.offsetLeft + sl.clientWidth / 2;
      const d = Math.abs(c - center);
      if (d < bestDist) { bestDist = d; best = i; }
    });
    return best;
  }

  _centerOn(index, behavior) {
    const slide = this._track.children[index];
    if (!slide) return;
    const left = slide.offsetLeft - (this._track.clientWidth - slide.clientWidth) / 2;
    this._track.scrollTo({ left, behavior: behavior || 'auto' });
  }

  _setActive(index) {
    Array.from(this._track.children).forEach((sl, i) => sl.classList.toggle('is-active', i === index));
  }

  _go(dir) {
    const max = this._track.children.length - 1;
    const i = Math.min(max, Math.max(0, this._currentIndex() + dir));
    this._centerOn(i, 'smooth');
  }

  _renderSlide(shot, i) {
    const slide = document.createElement('div');
    slide.className = 'vibe-gallery__slide';
    slide.dataset.shotIndex = String(i);
    const img = document.createElement('img');
    img.className = 'vibe-gallery__img';
    img.src = shot.baked_image;
    img.alt = shot.name || '';
    img.loading = 'lazy';
    slide.appendChild(img);
    slide.appendChild(this._renderCard(shot));
    // Click a dimmed neighbour (desktop coverflow) to bring it to centre.
    slide.addEventListener('click', (e) => {
      if (e.target.closest('.vibe-gallery__card')) return;
      if (!slide.classList.contains('is-active')) this._centerOn(i, 'smooth');
    });
    return slide;
  }

  _renderCard(shot) {
    const card = document.createElement('div');
    card.className = 'vibe-gallery__card';
    const pieces = shot.pieces || [];
    const first = pieces[0] || {};
    const isSet = pieces.length > 1;
    const priceCents = isSet ? shot.set_price_cents : first.price_cents;
    // A piece is addable only when it has a size_option for cart-resolve to work with.
    const addable = pieces.some((p) => p.size_option);

    // Static structure via innerHTML; dynamic text set via textContent to avoid XSS.
    card.innerHTML = `
      <img class="vibe-gallery__card-thumb" alt="">
      <div class="vibe-gallery__card-info">
        <span class="vibe-gallery__card-title"></span>
        <span class="vibe-gallery__card-meta"></span>
      </div>
      <button type="button" class="vibe-gallery__add"></button>`;

    card.querySelector('.vibe-gallery__card-thumb').src = first.thumb || '';
    // theSet/set_added strings are ONLY for multi-piece set shots; single-piece uses title (may be empty).
    card.querySelector('.vibe-gallery__card-title').textContent = isSet ? (shot.name || this._t.theSet) : (first.title || '');
    card.querySelector('.vibe-gallery__card-meta').textContent = this._money(priceCents);
    card.querySelector('.vibe-gallery__add').textContent = isSet ? this._t.addTheSet : this._t.add;

    card.querySelector('.vibe-gallery__add').addEventListener('click', (e) => {
      this._add(shot.pieces || [], e.currentTarget);
    });

    // Guard unpriceable/unresolvable pieces: disable Add and show a non-actionable marker.
    if (!addable) {
      const btn = card.querySelector('.vibe-gallery__add');
      btn.disabled = true;
      btn.textContent = '—';
    }

    return card;
  }

  _piecesToPositions(pieces) {
    return pieces.map((p, idx) => ({
      position_id: `vibe-${idx}`,
      product_id: p.product_id,
      size_option: p.size_option,
      framing_option: p.framing_option,
      border_option: p.border_option,
      frame_color: p.frame_color,
    }));
  }

  async _resolve(pieces) {
    const res = await fetch('/apps/chat-agent/gallery-wall/api/cart-resolve', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ positions: this._piecesToPositions(pieces) }),
    });
    if (!res.ok) throw new Error('resolve failed');
    const json = await res.json();
    const items = Array.isArray(json) ? json : (json.items || json.resolvedItems || []);
    if (!items.length) throw new Error('no resolved items');
    return items;
  }

  _toCartItems(resolved) {
    return resolved
      .filter((r) => r.variantId)
      .map((r) => {
        const id = String(r.variantId).replace('gid://shopify/ProductVariant/', '');
        const properties = {
          _size_option: r.sizeOption,
          _framing_option: r.framingOption,
          _frame_color: r.frameColor,
          _border_option: r.borderOption,
        };
        if (r.prodigiSku) properties._prodigi_sku = r.prodigiSku;
        if (r.prodigiColor) properties._prodigi_color = r.prodigiColor;
        if (r.prodigiWrap) properties._prodigi_wrap = r.prodigiWrap;
        if (r.borderPercent) properties._border_percent = r.borderPercent;
        if (r.mockImageHandle) properties._mock_image_handle = r.mockImageHandle;
        if (r.sizeLabel) properties['Size'] = r.sizeLabel;
        if (r.frameLabel) properties['Frame'] = r.frameLabel;
        if (r.borderLabel) properties['Border'] = r.borderLabel;
        return { id, quantity: 1, properties };
      });
  }

  async _add(pieces, button) {
    // Early-return if already in added state — second tap is a no-op (idempotent).
    if (button.classList.contains('is-added')) return;

    // Capture the label before mutating so we can restore it on error.
    const originalLabel = button.textContent;

    button.disabled = true;
    try {
      const resolved = await this._resolve(pieces);
      const items = this._toCartItems(resolved);
      if (!items.length) throw new Error('no addable items');
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) throw new Error('add failed');
      button.classList.add('is-added');
      button.textContent = items.length > 1 ? this._t.setAdded : this._t.added;
      this._showToast(this._t.addedToCart);
      // cart:update = ThemeEvents.cartUpdate = CartAddEvent.eventName — cart-drawer
      // opens on this and component-cart-items re-renders; more specific than
      // cart:refresh which only re-mounts the ATC-sheet whisper panel.
      // Theme listeners read event.detail.data.* WITHOUT guarding `.data`
      // (cart-icon.js:45, component-cart-items.js:340), so the detail shape is
      // required or they throw. Fetch the true total so the cart-icon badge
      // shows the right count; omit `sections` so component-cart-items falls
      // back to its own section re-render.
      let itemCount;
      try {
        const cart = await (await fetch('/cart.js')).json();
        itemCount = cart.item_count;
      } catch (_) { /* non-fatal: drawer still re-renders from the event */ }
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: { data: { itemCount } },
      }));
    } catch (e) {
      this._showToast(this._t.addError);
      // Restore to original actionable label (never blank, never stale 'is-added').
      button.classList.remove('is-added');
      button.textContent = originalLabel;
    } finally {
      button.disabled = false;
    }
  }

  _showToast(msg) {
    if (!this._toast) return;
    this._toast.textContent = msg;
    this._toast.classList.add('is-visible');
    clearTimeout(this._toastT);
    this._toastT = setTimeout(() => this._toast.classList.remove('is-visible'), 1600);
  }

  // Minimal focus trap: keep Tab focus within the overlay, wrapping at both ends.
  _trapFocus(e) {
    const focusables = this.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  open(index) {
    if (!this._built) this._build();
    this.hidden = false;
    document.body.style.overflow = 'hidden';
    document.removeEventListener('keydown', this._onKey);
    document.addEventListener('keydown', this._onKey);
    this._centerOn(index);
    this._setActive(index);
    this._updateDots(index);
    // Move focus into the overlay.
    this._close?.focus();
  }

  close() {
    this.hidden = true;
    document.body.style.overflow = '';
    document.removeEventListener('keydown', this._onKey);
    if (this._lastFocus) this._lastFocus.focus();
  }
}

customElements.define('vibe-gallery', VibeGallery);
